import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import API from '../utils/api';
import ProfileCard from '../components/ProfileCard';
import toast from 'react-hot-toast';
import './Dashboard.css';

const NAKSHATRAS = ['Ashwini','Bharani','Krittika','Rohini','Mrigashirsha','Ardra','Punarvasu','Pushya','Ashlesha','Magha','Purva Phalguni','Uttara Phalguni','Hastha','Chitra','Swati','Vishakha','Anuradha','Jyeshtha','Mula','Purva Ashadha','Uttara Ashadha','Shravana','Dhanishtha','Shatabhisha','Purva Bhadrapada','Uttara Bhadrapada','Revati'];
const RASIS = ['Mesha','Rishabha','Mithuna','Kataka','Simha','Kanya','Tula','Vrischika','Dhanus','Makara','Kumbha','Meena'];
const HORO_TYPES = ['Sutham','Seva','Rahu Ketu','Seva, Rahu Ketu'];
const BLOOD_GROUPS = ['A+','A-','B+','B-','AB+','AB-','O+','O-'];
const API_URL = process.env.REACT_APP_API_URL || '';

const TABS = [
  { id: 'home',      label: '🏠 Home',       ta: 'முகப்பு' },
  { id: 'browse',    label: '🔍 Browse',      ta: 'உலாவு' },
  { id: 'shortlist', label: '❤️ Saved',       ta: 'சேமித்தவை' },
  { id: 'contacts',  label: '📞 Contacts',    ta: 'தொடர்புகள்' },
  { id: 'profile',   label: '👤 My Profile',  ta: 'என் சுயவிவரம்' },
];

export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('home');
  const [myProfile, setMyProfile] = useState(null);
  const [profiles, setProfiles] = useState([]);
  const [shortlisted, setShortlisted] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [contactsReceived, setContactsReceived] = useState([]);
  const [contactsSent, setContactsSent] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ minAge:'', maxAge:'', star:'', rasi:'', search:'', sortBy:'match' });
  const [unread, setUnread] = useState(0);
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [editPhoto, setEditPhoto] = useState(null);
  const [editPhotoPreview, setEditPhotoPreview] = useState(null);
  const [editLoading, setEditLoading] = useState(false);

  useEffect(() => {
    if (!user) return navigate('/login');
    if (user.status !== 'approved') return navigate('/pending');
    loadMyProfile();
    loadNotifications();
  }, [user, navigate]);

  useEffect(() => {
    if (tab === 'browse') loadProfiles();
    if (tab === 'shortlist') loadShortlist();
    if (tab === 'contacts') loadContacts();
  }, [tab]);

  const loadMyProfile = async () => {
    try {
      const { data } = await API.get('/api/profiles/me');
      setMyProfile(data.profile);
      const prof = {...(data.profile || {})};
      if (prof.dob) prof.dob = new Date(prof.dob).toISOString().split('T')[0];
      setEditForm(prof);
    } catch {}
  };

  const loadProfiles = async () => {
    setLoading(true);
    try {
      const params = Object.fromEntries(Object.entries(filters).filter(([,v]) => v));
      const { data } = await API.get('/api/profiles/browse', { params });
      setProfiles(data.profiles || []);
    } catch { toast.error('Failed to load profiles'); }
    setLoading(false);
  };

  const loadShortlist = async () => {
    try {
      const { data } = await API.get('/api/shortlist/my');
      setShortlisted(data.profiles || []);
    } catch {}
  };

  const loadNotifications = async () => {
    try {
      const { data } = await API.get('/api/notifications');
      setNotifications(data.notifications || []);
      setUnread(data.notifications.filter(n => !n.is_read).length);
    } catch {}
  };

  const loadContacts = async () => {
    try {
      const [rec, sent] = await Promise.all([
        API.get('/api/contacts/received'),
        API.get('/api/contacts/sent')
      ]);
      setContactsReceived(rec.data.requests || []);
      setContactsSent(sent.data.requests || []);
    } catch {}
  };

  const markAllRead = async () => {
    await API.put('/api/notifications/mark-read');
    setNotifications(n => n.map(x => ({...x, is_read:1})));
    setUnread(0);
  };

  const handleContactResponse = async (reqId, status) => {
    try {
      await API.put(`/api/contacts/respond/${reqId}`, { status });
      toast.success(status === 'approved' ? 'Contact approved!' : 'Request declined.');
      loadContacts();
    } catch { toast.error('Failed to respond'); }
  };

  // ── EDIT PROFILE ──────────────────────────────────────────
  const setEF = (k, v) => setEditForm(f => ({...f, [k]: v}));

  const handleEditPhotoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setEditPhoto(file);
    setEditPhotoPreview(URL.createObjectURL(file));
  };

  const handleEditSubmit = async () => {
    setEditLoading(true);
    try {
      const fd = new FormData();
      const exclude = ['photo_path','horoscope_image_path','user_id','id','created_at','updated_at','age','profile_views','profile_visible'];
      Object.entries(editForm).forEach(([k,v]) => {
        if (!exclude.includes(k) && v !== null && v !== undefined && v !== '') {
          fd.append(k, v);
        }
      });
      if (editPhoto) fd.append('photo', editPhoto);
      await API.put('/api/profiles/update', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Profile updated successfully! ✅');
      setEditMode(false);
      setEditPhoto(null);
      setEditPhotoPreview(null);
      loadMyProfile();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update profile');
    }
    setEditLoading(false);
  };

  const handleVisibilityToggle = async () => {
    try {
      const newVal = myProfile.profile_visible ? 0 : 1;
      await API.put('/api/profiles/visibility', { visible: newVal });
      toast.success(newVal ? 'Profile is now visible' : 'Profile hidden from browse');
      setMyProfile(p => ({...p, profile_visible: newVal}));
    } catch { toast.error('Failed to update visibility'); }
  };

  const handlePasswordRequest = async () => {
    try {
      await API.post('/api/notifications/request-password-reset');
      const name = myProfile?.full_name || '';
      const phone = user?.phone || '';
      const message = `Hi, I would like to request a password change for my Suvaran Maran Matrimony account.%0AName: ${name}%0ARegistered Phone: ${phone}%0APlease help me reset my password.`;
      window.open(`https://wa.me/919842724724?text=${message}`, '_blank');
      toast.success('Request submitted! Admin will reset your password shortly 📲');
    } catch {
      const name = myProfile?.full_name || '';
      const phone = user?.phone || '';
      const message = `Hi, I would like to request a password change for my Suvaran Maran Matrimony account.%0AName: ${name}%0ARegistered Phone: ${phone}%0APlease help me reset my password.`;
      window.open(`https://wa.me/919842724724?text=${message}`, '_blank');
    }
  };

  return (
    <div className="dashboard-page">
      {/* SIDEBAR */}
      <aside className="dashboard-sidebar">
        <div className="ds-profile-mini">
          {myProfile?.photo_path ? (
            <img src={`${API_URL}${myProfile.photo_path}`} alt="" className="ds-photo" />
          ) : (
            <div className="ds-photo-placeholder">{myProfile?.profile_type === 'bride' ? '👰' : '🤵'}</div>
          )}
          <div>
            <div className="ds-name">{myProfile?.full_name || user?.phone}</div>
            <div className="ds-status badge badge-approved">✓ Approved</div>
          </div>
        </div>
        <nav className="ds-nav">
          {TABS.map(t => (
            <button key={t.id} className={`ds-nav-item ${tab === t.id && !editMode ? 'active' : ''}`}
              onClick={() => { setTab(t.id); setEditMode(false); }}>
              <span className="ds-nav-label">{t.label}</span>
              <span className="ds-nav-ta">{t.ta}</span>
              {t.id === 'contacts' && contactsReceived.filter(r=>r.status==='pending').length > 0 && (
                <span className="ds-badge">{contactsReceived.filter(r=>r.status==='pending').length}</span>
              )}
            </button>
          ))}
        </nav>
        <button className="ds-logout" onClick={() => { logout(); navigate('/'); }}>🚪 Logout</button>
      </aside>

      <main className="dashboard-main">

        {/* ── HOME TAB ── */}
        {tab === 'home' && !editMode && (
          <div className="dash-home">
            <div className="dash-greeting">
              <h1>வணக்கம், {myProfile?.full_name?.split(' ')[0] || 'Friend'}! 🙏</h1>
              <p>Welcome to Suvaran Maran Matrimony dashboard</p>
            </div>
            <div className="dash-stats">
              {[
                { icon:'👁️', label:'Profile Views',    value: myProfile?.profile_views || 0 },
                { icon:'❤️', label:'Shortlisted You',  value: 0 },
                { icon:'📞', label:'Contact Requests', value: contactsReceived.filter(r=>r.status==='pending').length },
                { icon:'🔔', label:'Notifications',    value: unread },
              ].map(s => (
                <div key={s.label} className="dash-stat-card">
                  <div className="dsc-icon">{s.icon}</div>
                  <div className="dsc-value">{s.value}</div>
                  <div className="dsc-label">{s.label}</div>
                </div>
              ))}
            </div>
            {myProfile && (
              <div className="card" style={{padding:'24px',marginBottom:'24px'}}>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'16px'}}>
                  <h2 style={{fontSize:'1.3rem'}}>My Profile Summary</h2>
                  <button className="btn btn-gold btn-sm" onClick={() => { setTab('profile'); setEditMode(true); }}>✏️ Edit Profile</button>
                </div>
                <div className="profile-summary-grid">
                  {[
                    ['Horoscope Type', myProfile.horoscope_type],
                    ['Birth Star', myProfile.birth_star],
                    ['Rasi', myProfile.rasi],
                    ['Age', myProfile.age ? `${myProfile.age} years` : null],
                    ['Height', myProfile.height],
                    ['Education', myProfile.education],
                  ].map(([k,v]) => v && (
                    <div key={k} className="psg-item"><span>{k}</span><strong>{v}</strong></div>
                  ))}
                </div>
              </div>
            )}
            <div className="card" style={{padding:'24px'}}>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'16px'}}>
                <h2 style={{fontSize:'1.3rem'}}>🔔 Notifications
                  {unread > 0 && <span className="notif-badge" style={{position:'static',display:'inline-flex',marginLeft:'8px'}}>{unread}</span>}
                </h2>
                {unread > 0 && <button className="btn btn-outline btn-sm" onClick={markAllRead}>Mark all read</button>}
              </div>
              {notifications.length === 0 ? (
                <div className="empty-state"><div className="icon">🔔</div><h3>No notifications yet</h3></div>
              ) : (
                <div className="notifications-list">
                  {notifications.slice(0,10).map(n => (
                    <div key={n.id} className={`notif-item ${!n.is_read ? 'unread' : ''}`}>
                      <div className="notif-dot" />
                      <div>
                        <strong>{n.title}</strong>
                        <p>{n.message}</p>
                        <span className="notif-time">{new Date(n.created_at).toLocaleString('en-IN')}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── BROWSE TAB ── */}
        {tab === 'browse' && !editMode && (
          <div className="dash-browse">
            <div className="browse-header">
              <h1>Browse Profiles</h1>
              <p>Compatible profiles for: <strong>{myProfile?.horoscope_type}</strong></p>
            </div>
            <div className="browse-filters card" style={{padding:'20px',marginBottom:'24px'}}>
              <div className="filter-grid">
                <input className="form-input" placeholder="Search name / city" value={filters.search} onChange={e => setFilters(f=>({...f,search:e.target.value}))} />
                <input className="form-input" type="number" placeholder="Min Age" value={filters.minAge} onChange={e => setFilters(f=>({...f,minAge:e.target.value}))} />
                <input className="form-input" type="number" placeholder="Max Age" value={filters.maxAge} onChange={e => setFilters(f=>({...f,maxAge:e.target.value}))} />
                <select className="form-select" value={filters.sortBy} onChange={e => setFilters(f=>({...f,sortBy:e.target.value}))}>
                  <option value="match">Sort: Match %</option>
                  <option value="age">Sort: Age</option>
                  <option value="">Sort: Recent</option>
                </select>
                <button className="btn btn-primary btn-sm" onClick={loadProfiles}>🔍 Search</button>
                <button className="btn btn-outline btn-sm" onClick={() => { setFilters({minAge:'',maxAge:'',star:'',rasi:'',search:'',sortBy:'match'}); setTimeout(loadProfiles,100); }}>Clear</button>
              </div>
            </div>
            {loading ? (
              <div className="loader"><div className="spinner" /><p>Loading profiles...</p></div>
            ) : profiles.length === 0 ? (
              <div className="empty-state"><div className="icon">🔍</div><h3>No profiles found</h3><p>Try adjusting your filters</p></div>
            ) : (
              <>
                <p style={{marginBottom:'16px',color:'var(--brown)'}}>Found <strong>{profiles.length}</strong> compatible profiles</p>
                <div className="profiles-grid">
                  {profiles.map(p => (
                    <ProfileCard key={p.user_id} profile={p}
                      onShortlistToggle={(id,added) => setProfiles(prev => prev.map(x => x.user_id===id ? {...x,is_shortlisted:added?1:0} : x))}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {/* ── SHORTLIST TAB ── */}
        {tab === 'shortlist' && !editMode && (
          <div className="dash-browse">
            <div className="browse-header"><h1>❤️ Saved Profiles</h1><p>Profiles you shortlisted</p></div>
            {shortlisted.length === 0 ? (
              <div className="empty-state"><div className="icon">❤️</div><h3>No saved profiles</h3><p>Browse and shortlist profiles you like</p><button className="btn btn-gold" onClick={() => setTab('browse')}>Browse Profiles</button></div>
            ) : (
              <div className="profiles-grid">
                {shortlisted.map(p => <ProfileCard key={p.user_id} profile={p} onShortlistToggle={(id) => setShortlisted(prev => prev.filter(x => x.user_id !== id))} />)}
              </div>
            )}
          </div>
        )}

        {/* ── CONTACTS TAB ── */}
        {tab === 'contacts' && !editMode && (
          <div className="dash-contacts">
            <h1>📞 Contact Requests</h1>
            <div className="contacts-section">
              <h2>Received Requests ({contactsReceived.length})</h2>
              {contactsReceived.length === 0 ? (
                <div className="empty-state"><div className="icon">📬</div><h3>No requests received</h3></div>
              ) : contactsReceived.map(r => (
                <div key={r.id} className="contact-req-card">
                  {r.sender_photo && <img src={`${API_URL}${r.sender_photo}`} alt="" className="cr-photo" />}
                  <div className="cr-info">
                    <strong>{r.sender_name}</strong>
                    <span>{r.age} yrs • {r.horoscope_type} • {r.native_place}</span>
                    <span className="cr-time">{new Date(r.created_at).toLocaleDateString('en-IN')}</span>
                  </div>
                  {r.status === 'pending' ? (
                    <div className="cr-actions">
                      <button className="btn btn-primary btn-sm" onClick={() => handleContactResponse(r.id,'approved')}>✅ Approve</button>
                      <button className="btn btn-outline btn-sm" onClick={() => handleContactResponse(r.id,'rejected')}>❌ Decline</button>
                    </div>
                  ) : (
                    <span className={`badge badge-${r.status==='approved'?'approved':'rejected'}`} style={{textTransform:'capitalize'}}>{r.status}</span>
                  )}
                </div>
              ))}
            </div>
            <div className="contacts-section">
              <h2>Sent Requests ({contactsSent.length})</h2>
              {contactsSent.length === 0 ? (
                <div className="empty-state"><div className="icon">📤</div><h3>No requests sent</h3></div>
              ) : contactsSent.map(r => (
                <div key={r.id} className="contact-req-card">
                  {r.receiver_photo && <img src={`${API_URL}${r.receiver_photo}`} alt="" className="cr-photo" />}
                  <div className="cr-info">
                    <strong>{r.receiver_name}</strong>
                    <span>{r.horoscope_type} • {r.native_place}</span>
                    {r.receiver_phone && <span className="cr-phone">📞 {r.receiver_phone}</span>}
                  </div>
                  <span className={`badge badge-${r.status==='approved'?'approved':r.status==='rejected'?'rejected':'pending'}`} style={{textTransform:'capitalize'}}>{r.status}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── MY PROFILE TAB (VIEW) ── */}
        {tab === 'profile' && !editMode && myProfile && (
          <div className="dash-profile">
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'24px',flexWrap:'wrap',gap:'12px'}}>
              <h1>👤 My Profile</h1>
              <div style={{display:'flex',gap:'10px',flexWrap:'wrap'}}>
                <button className="btn btn-outline btn-sm" onClick={handleVisibilityToggle}>
                  {myProfile.profile_visible ? '👁️ Hide Profile' : '👁️ Show Profile'}
                </button>
                <button className="btn btn-outline btn-sm" onClick={handlePasswordRequest}>
                  🔐 Request Password Change
                </button>
                <button className="btn btn-gold" onClick={() => setEditMode(true)}>✏️ Edit Profile</button>
              </div>
            </div>
            <div className="my-profile-grid">
              <div className="mpg-photo-col">
                {myProfile.photo_path ? (
                  <img src={`${API_URL}${myProfile.photo_path}`} alt="" className="mpg-photo" />
                ) : (
                  <div className="mpg-photo-ph">{myProfile.profile_type==='bride'?'👰':'🤵'}</div>
                )}
                <div className="mpg-badge-col">
                  <span className="badge badge-approved">✓ Approved</span>
                  <span className={`badge badge-${myProfile.horoscope_type==='Sutham'?'sutham':myProfile.horoscope_type==='Seva'?'seva':'rahuketu'}`}>{myProfile.horoscope_type}</span>
                  <span className={`badge ${myProfile.profile_visible ? 'badge-approved' : 'badge-pending'}`}>{myProfile.profile_visible ? '👁️ Visible' : '🙈 Hidden'}</span>
                </div>
              </div>
              <div className="mpg-details">
                <h2>{myProfile.full_name}</h2>
                {myProfile.full_name_tamil && <p style={{color:'var(--brown)',fontStyle:'italic'}}>{myProfile.full_name_tamil}</p>}
                <div className="mpg-info-grid">
                  {[
                    ['God Name',myProfile.god_name],['Date of Birth', myProfile.dob ? new Date(myProfile.dob).toLocaleDateString('en-IN',{day:'2-digit',month:'long',year:'numeric'}) : null],
                    ['Blood Group',myProfile.blood_group],['Height',myProfile.height],
                    ['Birth Star',myProfile.birth_star],['Rasi',myProfile.rasi],
                    ['Lagnam',myProfile.lagnam],['Education',myProfile.education],
                    ['Profession',myProfile.job_profession],['Income',myProfile.income],
                    ['Father',myProfile.father_name],['Mother',myProfile.mother_name],
                    ['Siblings',myProfile.siblings_count],['Property',myProfile.has_property],
                    ['Native Place',myProfile.native_place],['Caste',myProfile.caste_subdivision],
                    ['Address',myProfile.address],['Alt Phone',myProfile.alternate_phone],
                  ].filter(([,v]) => v).map(([k,v]) => (
                    <div key={k} className="mpg-info-item"><span>{k}</span><strong>{v}</strong></div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── EDIT PROFILE FORM ── */}
        {editMode && (
          <div className="dash-edit">
            <div style={{display:'flex',alignItems:'center',gap:'12px',marginBottom:'28px',flexWrap:'wrap'}}>
              <button className="btn btn-outline btn-sm" onClick={() => { setEditMode(false); setEditPhoto(null); setEditPhotoPreview(null); }}>← Back</button>
              <h1 style={{margin:0}}>✏️ Edit My Profile</h1>
            </div>

            <div className="edit-sections">

              {/* Photo */}
              <div className="edit-section card" style={{padding:'24px',marginBottom:'20px'}}>
                <h3 className="edit-section-title">📸 Profile Photo</h3>
                <div style={{display:'flex',alignItems:'center',gap:'24px',flexWrap:'wrap'}}>
                  <div className="edit-photo-wrap" onClick={() => document.getElementById('edit-photo-input').click()}>
                    {editPhotoPreview || myProfile?.photo_path ? (
                      <img src={editPhotoPreview || `${API_URL}${myProfile.photo_path}`} alt="" className="edit-photo-preview" />
                    ) : (
                      <div className="edit-photo-placeholder">📸<br/><small>Click to upload</small></div>
                    )}
                    <div className="edit-photo-overlay">Change Photo</div>
                    <input id="edit-photo-input" type="file" accept="image/*" hidden onChange={handleEditPhotoChange} />
                  </div>
                  <div>
                    <p style={{fontSize:'0.88rem',color:'var(--brown)'}}>Recommended: 9:16 portrait ratio</p>
                    <p style={{fontSize:'0.82rem',color:'var(--text-light)'}}>e.g. 900×1600 pixels</p>
                  </div>
                </div>
              </div>

              {/* Personal Details */}
              <div className="edit-section card" style={{padding:'24px',marginBottom:'20px'}}>
                <h3 className="edit-section-title">👤 Personal Details</h3>
                <div className="edit-form-grid">
                  <div className="form-group">
                    <label className="form-label">God Name / தெய்வம்</label>
                    <input className="form-input" value={editForm.god_name||''} onChange={e=>setEF('god_name',e.target.value)} placeholder="e.g. Murugan" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Full Name / முழு பெயர்</label>
                    <input className="form-input" value={editForm.full_name||''} onChange={e=>setEF('full_name',e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Name in Tamil / தமிழில் பெயர்</label>
                    <input className="form-input" value={editForm.full_name_tamil||''} onChange={e=>setEF('full_name_tamil',e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Date of Birth / பிறந்த தேதி</label>
                    <input className="form-input" type="date" value={editForm.dob||''} onChange={e=>setEF('dob',e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Time of Birth / பிறந்த நேரம்</label>
                    <input className="form-input" type="time" value={editForm.tob||''} onChange={e=>setEF('tob',e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Blood Group / இரத்த வகை</label>
                    <select className="form-select" value={editForm.blood_group||''} onChange={e=>setEF('blood_group',e.target.value)}>
                      <option value="">Select</option>
                      {BLOOD_GROUPS.map(b=><option key={b}>{b}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Height / உயரம்</label>
                    <input className="form-input" value={editForm.height||''} onChange={e=>setEF('height',e.target.value)} placeholder="e.g. 5'8&quot;" />
                  </div>
                </div>
              </div>

              {/* Horoscope */}
              <div className="edit-section card" style={{padding:'24px',marginBottom:'20px'}}>
                <h3 className="edit-section-title">🌟 Horoscope Details</h3>
                <div className="edit-form-grid">
                  <div className="form-group">
                    <label className="form-label">Birth Star / நட்சத்திரம்</label>
                    <select className="form-select" value={editForm.birth_star||''} onChange={e=>setEF('birth_star',e.target.value)}>
                      <option value="">Select Nakshatra</option>
                      {NAKSHATRAS.map((n,i)=><option key={n} value={n}>{i+1}. {n}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Rasi / ராசி</label>
                    <select className="form-select" value={editForm.rasi||''} onChange={e=>setEF('rasi',e.target.value)}>
                      <option value="">Select Rasi</option>
                      {RASIS.map(r=><option key={r}>{r}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Lagnam / லக்னம்</label>
                    <select className="form-select" value={editForm.lagnam||''} onChange={e=>setEF('lagnam',e.target.value)}>
                      <option value="">Select Lagnam</option>
                      {RASIS.map(r=><option key={r}>{r}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Horoscope Type / ஜாதக வகை</label>
                    <select className="form-select" value={editForm.horoscope_type||''} onChange={e=>setEF('horoscope_type',e.target.value)}>
                      <option value="">Select Type</option>
                      {HORO_TYPES.map(t=><option key={t}>{t}</option>)}
                    </select>
                  </div>
                </div>
              </div>

              {/* Education & Career */}
              <div className="edit-section card" style={{padding:'24px',marginBottom:'20px'}}>
                <h3 className="edit-section-title">🎓 Education & Career</h3>
                <div className="edit-form-grid">
                  <div className="form-group">
                    <label className="form-label">Education / கல்வி</label>
                    <input className="form-input" value={editForm.education||''} onChange={e=>setEF('education',e.target.value)} placeholder="e.g. B.Tech" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Profession / தொழில்</label>
                    <input className="form-input" value={editForm.job_profession||''} onChange={e=>setEF('job_profession',e.target.value)} placeholder="e.g. Engineer" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Workplace / பணிமனை</label>
                    <input className="form-input" value={editForm.workplace||''} onChange={e=>setEF('workplace',e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Income / வருமானம்</label>
                    <select className="form-select" value={editForm.income||''} onChange={e=>setEF('income',e.target.value)}>
                      <option value="">Select</option>
                      {['Below ₹2L','₹2L - ₹4L','₹4L - ₹6L','₹6L - ₹10L','₹10L - ₹15L','₹15L - ₹25L','Above ₹25L'].map(i=><option key={i}>{i}</option>)}
                    </select>
                  </div>
                </div>
              </div>

              {/* Family */}
              <div className="edit-section card" style={{padding:'24px',marginBottom:'20px'}}>
                <h3 className="edit-section-title">👨‍👩‍👧 Family Details</h3>
                <div className="edit-form-grid">
                  <div className="form-group">
                    <label className="form-label">Father's Name / தந்தை பெயர்</label>
                    <input className="form-input" value={editForm.father_name||''} onChange={e=>setEF('father_name',e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Mother's Name / தாயார் பெயர்</label>
                    <input className="form-input" value={editForm.mother_name||''} onChange={e=>setEF('mother_name',e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Siblings Count / உடன்பிறந்தோர்</label>
                    <input className="form-input" type="number" min="0" value={editForm.siblings_count||0} onChange={e=>setEF('siblings_count',e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Siblings Married / திருமணமானவர்</label>
                    <input className="form-input" type="number" min="0" value={editForm.siblings_married||0} onChange={e=>setEF('siblings_married',e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Caste / சாதி</label>
                    <input className="form-input" value={editForm.caste_subdivision||''} onChange={e=>setEF('caste_subdivision',e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Property / சொத்து</label>
                    <select className="form-select" value={editForm.has_property||'No'} onChange={e=>setEF('has_property',e.target.value)}>
                      <option value="Yes">Yes / ஆம்</option>
                      <option value="No">No / இல்லை</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Location */}
              <div className="edit-section card" style={{padding:'24px',marginBottom:'20px'}}>
                <h3 className="edit-section-title">📍 Location Details</h3>
                <div className="edit-form-grid">
                  <div className="form-group">
                    <label className="form-label">Place of Birth / பிறந்த ஊர்</label>
                    <input className="form-input" value={editForm.place_of_birth||''} onChange={e=>setEF('place_of_birth',e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Native Place / சொந்த ஊர்</label>
                    <input className="form-input" value={editForm.native_place||''} onChange={e=>setEF('native_place',e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Alternate Phone / மாற்று எண்</label>
                    <input className="form-input" type="tel" value={editForm.alternate_phone||''} onChange={e=>setEF('alternate_phone',e.target.value.replace(/\D/,'').slice(0,10))} />
                  </div>
                  <div className="form-group" style={{gridColumn:'1/-1'}}>
                    <label className="form-label">Address / முகவரி</label>
                    <textarea className="form-textarea" value={editForm.address||''} onChange={e=>setEF('address',e.target.value)} rows={3} />
                  </div>
                </div>
              </div>

              {/* Save Button */}
              <div style={{display:'flex',gap:'12px',justifyContent:'flex-end',marginTop:'8px',paddingBottom:'40px'}}>
                <button className="btn btn-outline" onClick={() => { setEditMode(false); setEditPhoto(null); setEditPhotoPreview(null); }}>
                  Cancel
                </button>
                <button className="btn btn-gold btn-lg" onClick={handleEditSubmit} disabled={editLoading}>
                  {editLoading ? <><span className="spinner" style={{width:18,height:18}} /> Saving...</> : '💾 Save Changes'}
                </button>
              </div>
            </div>
          </div>
        )}

      </main>
    </div>
  );
}